import { useQuery } from '@tanstack/react-query'
import { useState, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { PlusIcon, EyeIcon, ChevronUpIcon, ChevronDownIcon, MagnifyingGlassIcon } from '@heroicons/react/20/solid'
import { CubeIcon } from '@heroicons/react/24/outline'
import { twinApi, type TwinProject } from '../../lib/api'
import { TwinStatusBadge } from '../../components/TwinViewer'
import { useTranslation } from '../../hooks/useTranslation'

const STATUSES = ['draft', 'pending', 'processing', 'ready', 'failed'] as const
type TwinStatus = typeof STATUSES[number]
type SortField = 'name' | 'status' | 'createdAt' | 'updatedAt'
type SortDirection = 'asc' | 'desc'

const STATUS_ORDER: Record<TwinStatus, number> = {
  draft: 0,
  processing: 1,
  pending: 2,
  ready: 3,
  failed: 4,
}

export function TwinProjectsListPage() {
  const { t } = useTranslation()
  const [statusFilter, setStatusFilter] = useState<TwinStatus | 'all'>('all')
  const [searchQuery, setSearchQuery] = useState('')
  const [sortField, setSortField] = useState<SortField>('createdAt')
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc')

  const { data: projects, isLoading, error } = useQuery({
    queryKey: ['twin-projects'],
    queryFn: () => twinApi.listProjects(),
    retry: 1,
  })

  // Check if Twin Engine is available
  const { data: health } = useQuery({
    queryKey: ['twin-health'],
    queryFn: () => twinApi.health(),
    retry: false,
  })

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
    } else {
      setSortField(field)
      setSortDirection(field === 'createdAt' || field === 'updatedAt' ? 'desc' : 'asc')
    }
  }

  // Client-side filtering and sorting (Twin API doesn't support server-side)
  const filteredAndSortedProjects = useMemo(() => {
    if (!projects) return []

    let result = [...projects]

    // Filter by status
    if (statusFilter !== 'all') {
      result = result.filter(p => p.status === statusFilter)
    }

    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase()
      result = result.filter(p =>
        p.name.toLowerCase().includes(query) ||
        p.description?.toLowerCase().includes(query)
      )
    }

    // Sort
    result.sort((a, b) => {
      let comparison = 0

      switch (sortField) {
        case 'name':
          comparison = a.name.localeCompare(b.name)
          break
        case 'status':
          comparison = STATUS_ORDER[a.status] - STATUS_ORDER[b.status]
          break
        case 'createdAt':
          comparison = new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
          break
        case 'updatedAt':
          comparison = new Date(a.updatedAt).getTime() - new Date(b.updatedAt).getTime()
          break
      }

      return sortDirection === 'asc' ? comparison : -comparison
    })

    return result
  }, [projects, statusFilter, searchQuery, sortField, sortDirection])

  // Compute stats from projects
  const stats = useMemo(() => {
    if (!projects) return { total: 0, pending: 0, processing: 0, ready: 0, failed: 0 }
    return {
      total: projects.length,
      pending: projects.filter(p => p.status === 'pending').length,
      processing: projects.filter(p => p.status === 'processing').length,
      ready: projects.filter(p => p.status === 'ready').length,
      failed: projects.filter(p => p.status === 'failed').length,
    }
  }, [projects])

  const SortableHeader = ({ field, children }: { field: SortField; children: React.ReactNode }) => {
    const isActive = sortField === field
    return (
      <th
        className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 cursor-pointer hover:bg-gray-100 select-none"
        onClick={() => handleSort(field)}
      >
        <div className="flex items-center gap-1">
          {children}
          <div className="flex flex-col -space-y-1">
            <ChevronUpIcon
              className={`h-3 w-3 ${isActive && sortDirection === 'asc' ? 'text-gray-900' : 'text-gray-300'}`}
            />
            <ChevronDownIcon
              className={`h-3 w-3 ${isActive && sortDirection === 'desc' ? 'text-gray-900' : 'text-gray-300'}`}
            />
          </div>
        </div>
      </th>
    )
  }

  // Show error state based on error type
  if (error || health?.status === 'error') {
    // Determine error type for appropriate messaging
    const errorMessage = error instanceof Error ? error.message : String(error)
    const isInternalError = errorMessage.includes('500') || errorMessage.includes('INTERNAL_ERROR')
    const isServiceUnavailable = health?.twinEngine === 'unreachable' || errorMessage.includes('503') || errorMessage.includes('502')

    const errorTitle = isInternalError
      ? t('twin.viewer.internalError')
      : t('twin.viewer.serviceUnavailable')
    const errorDesc = isInternalError
      ? t('twin.viewer.internalErrorDesc')
      : t('twin.viewer.serviceUnavailableDesc')

    return (
      <div className="p-4 lg:p-8">
        <div className="mx-auto max-w-7xl">
          <div className="rounded-lg border border-dashed border-red-300 bg-red-50 p-12 text-center">
            <CubeIcon className="mx-auto h-12 w-12 text-red-400" />
            <h3 className="mt-2 text-sm font-semibold text-gray-900">{errorTitle}</h3>
            <p className="mt-1 text-sm text-gray-500">{errorDesc}</p>
          </div>
        </div>
      </div>
    )
  }

  if (isLoading) {
    return (
      <div className="p-4 lg:p-8">
        <div className="mx-auto max-w-7xl">
          <div className="animate-pulse space-y-4">
            <div className="h-8 bg-gray-200 rounded w-1/4"></div>
            <div className="h-4 bg-gray-200 rounded w-1/3"></div>
            <div className="h-64 bg-gray-200 rounded"></div>
          </div>
        </div>
      </div>
    )
  }

  // Empty state
  if (!projects || projects.length === 0) {
    return (
      <div className="p-4 lg:p-8">
        <div className="mx-auto max-w-7xl">
          <div className="sm:flex sm:items-center sm:justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">{t('twin.projects.title')}</h1>
              <p className="mt-1 text-sm text-gray-500">{t('twin.projects.description')}</p>
            </div>
            <div className="mt-4 sm:mt-0">
              <Link
                to="/twin/new"
                className="inline-flex items-center gap-x-2 rounded-md bg-brand-500 px-3.5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-brand-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-500"
              >
                <PlusIcon className="-ml-0.5 size-5" aria-hidden="true" />
                {t('twin.projects.create')}
              </Link>
            </div>
          </div>

          <div className="mt-8">
            <div className="rounded-lg border border-dashed border-gray-300 p-12 text-center">
              <CubeIcon className="mx-auto size-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-semibold text-gray-900">{t('twin.projects.empty')}</h3>
              <p className="mt-1 text-sm text-gray-500">{t('twin.upload.description')}</p>
              <div className="mt-6">
                <Link
                  to="/twin/new"
                  className="inline-flex items-center gap-x-2 rounded-md bg-brand-500 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-brand-600"
                >
                  <PlusIcon className="-ml-0.5 size-5" aria-hidden="true" />
                  {t('twin.projects.create')}
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <>
      {/* Secondary column - Actions and filters */}
      <aside className="fixed inset-y-4 left-72 hidden w-[calc((100vw-18rem-2rem)/3)] overflow-y-auto bg-white px-4 py-6 sm:px-6 lg:px-8 xl:block rounded-xl shadow-[4px_4px_12px_rgba(0,0,0,0.08)]">
        <div className="space-y-6">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">{t('twin.projects.title')}</h2>
            <p className="mt-1 text-sm text-gray-500">{t('twin.projects.description')}</p>
          </div>

          <Link
            to="/twin/new"
            className="w-full flex items-center justify-center gap-x-2 rounded-md px-3 py-2 text-center text-sm font-semibold text-white shadow-sm hover:opacity-90 bg-brand-500"
          >
            <PlusIcon className="h-5 w-5" />
            {t('twin.projects.create')}
          </Link>

          {/* Search */}
          <div className="bg-white p-4 rounded-lg shadow-sm ring-1 ring-gray-900/5">
            <h3 className="text-sm font-medium text-gray-900 mb-3">{t('common.search')}</h3>
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder={t('twin.projects.search')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 sm:text-sm"
              />
            </div>
          </div>

          {/* Status Filter */}
          <div className="bg-white p-4 rounded-lg shadow-sm ring-1 ring-gray-900/5">
            <h3 className="text-sm font-medium text-gray-900 mb-3">{t('common.status')}</h3>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setStatusFilter('all')}
                className={`px-3 py-1.5 text-xs font-medium rounded-full transition-colors ${
                  statusFilter === 'all'
                    ? 'bg-gray-900 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {t('common.all')}
              </button>
              {STATUSES.map(status => (
                <button
                  key={status}
                  onClick={() => setStatusFilter(status)}
                  className={`px-3 py-1.5 text-xs font-medium rounded-full transition-colors ${
                    statusFilter === status
                      ? 'bg-gray-900 text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {t(`twin.status.${status}`)}
                </button>
              ))}
            </div>
          </div>

          {/* Stats */}
          <div className="bg-white p-4 rounded-lg shadow-sm ring-1 ring-gray-900/5">
            <h3 className="text-sm font-medium text-gray-900 mb-3">{t('common.quickInfo')}</h3>
            <dl className="space-y-2">
              <div className="flex justify-between text-sm">
                <dt className="text-gray-500">{t('twin.status.ready')}</dt>
                <dd className="font-medium text-green-600">{stats.ready}</dd>
              </div>
              <div className="flex justify-between text-sm">
                <dt className="text-gray-500">{t('twin.status.processing')}</dt>
                <dd className="font-medium text-yellow-600">{stats.processing}</dd>
              </div>
              <div className="flex justify-between text-sm">
                <dt className="text-gray-500">{t('twin.status.pending')}</dt>
                <dd className="font-medium text-gray-600">{stats.pending}</dd>
              </div>
              <div className="flex justify-between text-sm">
                <dt className="text-gray-500">{t('twin.status.failed')}</dt>
                <dd className="font-medium text-red-600">{stats.failed}</dd>
              </div>
              <div className="flex justify-between text-sm border-t pt-2">
                <dt className="text-gray-500">{t('common.all')}</dt>
                <dd className="font-medium text-gray-900">{stats.total}</dd>
              </div>
            </dl>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="xl:pl-[calc((100vw-18rem-2rem)/3)] xl:ml-4 p-4 xl:p-0 xl:pr-4 xl:py-4">
        <div className="bg-white rounded-xl shadow-[4px_4px_12px_rgba(0,0,0,0.08)] min-h-[calc(100vh-2rem)] px-4 py-10 sm:px-6 lg:px-8 lg:py-6 overflow-hidden">
          {/* Mobile header */}
          <div className="xl:hidden mb-6">
            <h1 className="text-2xl font-semibold text-gray-900">{t('twin.projects.title')}</h1>
            <Link
              to="/twin/new"
              className="mt-4 w-full flex items-center justify-center gap-x-2 rounded-md px-3 py-2 text-center text-sm font-semibold text-white shadow-sm hover:opacity-90 bg-brand-500"
            >
              <PlusIcon className="h-5 w-5" />
              {t('twin.projects.create')}
            </Link>

            {/* Mobile search */}
            <div className="mt-4 relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder={t('twin.projects.search')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 sm:text-sm"
              />
            </div>

            {/* Mobile status filter */}
            <div className="mt-4 flex flex-wrap gap-2">
              <button
                onClick={() => setStatusFilter('all')}
                className={`px-3 py-1.5 text-xs font-medium rounded-full transition-colors ${
                  statusFilter === 'all'
                    ? 'bg-gray-900 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {t('common.all')}
              </button>
              {STATUSES.map(status => (
                <button
                  key={status}
                  onClick={() => setStatusFilter(status)}
                  className={`px-3 py-1.5 text-xs font-medium rounded-full transition-colors ${
                    statusFilter === status
                      ? 'bg-gray-900 text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {t(`twin.status.${status}`)}
                </button>
              ))}
            </div>
          </div>

          {/* Results info */}
          {(searchQuery || statusFilter !== 'all') && (
            <div className="mb-4 text-sm text-gray-500">
              {t('common.showingOf', { count: filteredAndSortedProjects.length.toString(), total: (projects?.length || 0).toString() })}
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="ml-2 text-brand-600 hover:text-brand-700"
                >
                  {t('common.clear')}
                </button>
              )}
            </div>
          )}

          {/* Table */}
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead>
                <tr>
                  <SortableHeader field="name">{t('common.name')}</SortableHeader>
                  <SortableHeader field="status">{t('common.status')}</SortableHeader>
                  <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">{t('common.description')}</th>
                  <SortableHeader field="createdAt">{t('fields.createdAt')}</SortableHeader>
                  <SortableHeader field="updatedAt">{t('fields.date')}</SortableHeader>
                  <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">{t('common.actions')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredAndSortedProjects.map((project) => (
                  <tr key={project.id} className="hover:bg-gray-50">
                    <td className="whitespace-nowrap px-3 py-4 text-sm font-medium text-gray-900">
                      <Link to={`/twin/${project.id}`} className="hover:text-brand-600 flex items-center gap-2">
                        <CubeIcon className="h-4 w-4 text-gray-400" />
                        {project.name}
                      </Link>
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm">
                      <TwinStatusBadge status={project.status} />
                    </td>
                    <td className="px-3 py-4 text-sm text-gray-500 max-w-xs truncate">
                      {project.description || '-'}
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                      {new Date(project.createdAt).toLocaleDateString()}
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                      {new Date(project.updatedAt).toLocaleDateString()}
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm">
                      <Link
                        to={`/twin/${project.id}`}
                        className="text-brand-600 hover:text-brand-900"
                        title={t('common.view')}
                      >
                        <EyeIcon className="h-5 w-5" />
                      </Link>
                    </td>
                  </tr>
                ))}
                {filteredAndSortedProjects.length === 0 && (
                  <tr>
                    <td colSpan={6} className="px-3 py-8 text-center text-sm text-gray-500">
                      {t('common.noResults')}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </>
  )
}
